import { useAppStore } from '../../store';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, subDays, isToday } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { getTodayIso, getWeekScope, shiftIsoDate } from '../../lib/dateScope';

interface DateNavProps {
  taskCount?: number;
  contextLabel?: string;
}

export function DateNav({ taskCount, contextLabel }: DateNavProps) {
  const { selectedDate, setSelectedDate } = useAppStore();
  const date = new Date(selectedDate + 'T00:00:00');

  const goBack = () => setSelectedDate(format(subDays(date, 1), 'yyyy-MM-dd'));
  const goForward = () => setSelectedDate(format(addDays(date, 1), 'yyyy-MM-dd'));
  const goToday = () => setSelectedDate(format(new Date(), 'yyyy-MM-dd'));

  return (
    <div className="flex items-center gap-1 shrink-0">
      <button onClick={goBack} aria-label="Previous day" className="p-2 rounded-lg interactive-ghost text-text-muted hover:text-text-secondary">
        <ChevronLeft size={14} />
      </button>
      <button
        onClick={goToday}
        className="px-3 py-1 rounded-lg interactive-ghost text-title font-semibold text-text-primary tabular-nums min-w-24 text-center"
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={selectedDate}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="block"
          >
            {isToday(date) ? (
              <span className="text-accent font-extrabold">Today</span>
            ) : (
              format(date, 'EEE, MMM d')
            )}
          </motion.span>
        </AnimatePresence>
      </button>
      <button onClick={goForward} aria-label="Next day" className="p-2 rounded-lg interactive-ghost text-text-muted hover:text-text-secondary">
        <ChevronRight size={14} />
      </button>
      {taskCount !== undefined && (
        <AnimatePresence mode="wait">
          <motion.span
            key={`${taskCount}-${contextLabel}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="text-caption text-text-muted ml-1"
          >
            · {taskCount} {contextLabel}
          </motion.span>
        </AnimatePresence>
      )}
    </div>
  );
}

export function WeekNav() {
  const { selectedDate, setSelectedDate } = useAppStore();
  const scope = getWeekScope(selectedDate);

  const goBack = () => setSelectedDate(shiftIsoDate(selectedDate, -7));
  const goForward = () => setSelectedDate(shiftIsoDate(selectedDate, 7));
  const goThisWeek = () => setSelectedDate(getTodayIso());

  return (
    <div className="flex items-center gap-1 shrink-0">
      <button onClick={goBack} aria-label="Previous week" className="p-2 rounded-lg interactive-ghost text-text-muted hover:text-text-secondary">
        <ChevronLeft size={14} />
      </button>
      <button
        onClick={goThisWeek}
        className="px-3 py-1 rounded-lg interactive-ghost text-title font-semibold text-text-primary tabular-nums min-w-44 text-center"
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={selectedDate}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="block"
          >
            {scope.headerLabel}
          </motion.span>
        </AnimatePresence>
      </button>
      <button onClick={goForward} aria-label="Next week" className="p-2 rounded-lg interactive-ghost text-text-muted hover:text-text-secondary">
        <ChevronRight size={14} />
      </button>
    </div>
  );
}
